import { useState, type FormEvent, type ReactNode, type SVGProps } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';

// ─────────────────────────────────────────────────────────────
// LoginPage — Variation C ("Image Left / White Right")
// All colors/fonts come from src/styles/tokens.css via Tailwind.
// To restyle: edit tokens.css, not this file.
// ─────────────────────────────────────────────────────────────

const UserIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" width={17} height={17} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" {...p}>
    <circle cx="10" cy="7" r="3.5" />
    <path d="M3 18c0-3.5 3-6 7-6s7 2.5 7 6" />
  </svg>
);
const LockIcon = (p: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" width={17} height={17} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" {...p}>
    <rect x="4" y="9" width="12" height="8" rx="1.5" />
    <path d="M7 9V6.5a3 3 0 016 0V9" />
  </svg>
);

interface LoginFieldProps {
  id: string;
  label: string;
  placeholder: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  icon: ReactNode;
  autoComplete?: string;
}

function LoginField({ id, label, placeholder, type = 'text', value, onChange, icon, autoComplete }: LoginFieldProps) {
  return (
    <div className="mb-[18px]">
      <label
        htmlFor={id}
        className="block text-text-mid mb-[7px] font-medium tracking-[0.3px]"
        style={{ fontSize: 11.5 }}
      >
        {label}
      </label>
      <div className="flex items-center gap-2.5 px-3.5 h-[46px] bg-panel border-[1.5px] border-border rounded focus-within:border-accent-bright transition-colors duration-fast">
        <span className="text-text-dim flex">{icon}</span>
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 w-full bg-transparent border-none outline-none text-base text-text placeholder:text-text-dim"
        />
      </div>
    </div>
  );
}

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const login = useAuthStore((s) => s.login);
  const isLoading = useAuthStore((s) => s.isLoading);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login({ username, password });
      const user = useAuthStore.getState().user;
      navigate(user?.role === 'SuperAdmin' ? '/dashboard' : '/dashboard/wells');
    } catch {
      setError('Username or password is incorrect');
    }
  };

  return (
    <div className="w-screen h-screen flex overflow-hidden bg-bg font-sans">
      {/* ── Left — Hero image (hidden < md) ────────────────────── */}
      <div className="hidden md:block relative overflow-hidden flex-shrink-0" style={{ width: 680 }}>
        <img
          src="/login-well-bg.png"
          alt="Geonomic well site"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark vertical gradient */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(12,21,32,0.6) 0%, rgba(12,21,32,0.4) 50%, rgba(12,21,32,0.75) 100%)',
          }}
        />
        {/* Logo (top-left, white) */}
        <div className="absolute top-11 left-11">
          <img
            src="/geonomic-logo-v2.png"
            alt="Geonomic Technologies"
            className="block w-auto"
            style={{ height: 32, filter: 'brightness(0) invert(1)' }}
          />
        </div>
        {/* Hero caption (bottom-left) */}
        <div className="absolute bottom-11 left-11 right-11">
          <div className="font-display text-2xl text-white font-medium tracking-[-0.4px] leading-[1.3] mb-2.5">
            Advanced downhole
            <br />
            intelligence platform
          </div>
          <p className="text-white/50 max-w-[380px]" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
            Real-time monitoring, 3D modeling, and predictive analytics for drilling operations.
          </p>
        </div>
      </div>

      {/* ── Right — White form (centered) ──────────────────────── */}
      <div className="flex-1 flex flex-col items-center justify-center relative px-8 sm:px-16 py-12">
        <div className="w-full max-w-[380px]">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mb-6 flex justify-center">
              <img
                src="/geonomic-logo-v2.png"
                alt="Geonomic Technologies"
                className="block w-auto"
                style={{ height: 38 }}
              />
            </div>
            <h1 className="font-display font-medium text-text mb-1.5" style={{ fontSize: 32, letterSpacing: -0.6 }}>
              Sign In
            </h1>
            <p className="text-text-dim text-base">Sign in to your account</p>
          </div>

          {/* Error banner */}
          {error && (
            <div
              role="alert"
              className="mb-[18px] px-3.5 py-2.5 rounded bg-red-50 text-bad text-sm border border-red-200"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <LoginField
              id="username"
              label="Username"
              placeholder="Enter your username"
              autoComplete="username"
              value={username}
              onChange={setUsername}
              icon={<UserIcon />}
            />
            <LoginField
              id="password"
              label="Password"
              placeholder="Enter your password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              icon={<LockIcon />}
            />

            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-2 py-3.5 rounded bg-accent text-white font-semibold cursor-pointer transition-colors duration-fast hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-85"
              style={{ fontSize: 14.5 }}
            >
              {isLoading ? 'Signing in…' : 'Login'}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="absolute bottom-7 font-mono text-text-dim tracking-[0.4px]" style={{ fontSize: 11 }}>
          © {new Date().getFullYear()} Geonomic Technologies Inc.
        </div>
      </div>
    </div>
  );
}
